import { useState } from 'react';
import { Search, ScanBarcode, Eye, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import { useAssignmentScans } from '../hooks/useAssignmentScans';
import { useEmployees } from '../hooks/useEmployees';
import type {
  AssignmentScan,
  AssignmentScanFilters,
} from '../types/assignment-scan.types';
import { AssignmentScanSyncStatus } from '../types/assignment-scan.types';

export function AssignmentScans() {
  const [filters, setFilters] = useState<AssignmentScanFilters>({
    page: 1,
    page_size: 20,
    sort: 'scanned_at:desc',
  });

  const [searchPatientIin, setSearchPatientIin] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('all');
  const [selectedSyncStatus, setSelectedSyncStatus] = useState<string>('all');

  const [selectedScan, setSelectedScan] = useState<AssignmentScan | null>(null);

  const { data, isLoading, error, refetch } = useAssignmentScans(filters);
  const {
    data: employeesData,
    isLoading: isLoadingEmployees,
  } = useEmployees({ page: 1, limit: 100, isActive: true });

  const handleApplyFilters = () => {
    setFilters({
      ...filters,
      patient_iin: searchPatientIin || undefined,
      employee_id: selectedEmployeeId !== 'all' ? selectedEmployeeId : undefined,
      sync_status: selectedSyncStatus !== 'all' ? selectedSyncStatus : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page: 1,
    });
  };

  const handleResetFilters = () => {
    setSearchPatientIin('');
    setSelectedEmployeeId('all');
    setSelectedSyncStatus('all');
    setDateFrom('');
    setDateTo('');
    setFilters({
      page: 1,
      page_size: 20,
      sort: 'scanned_at:desc',
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const formatDateTime = (dateString: string): string => {
    try {
      return format(parseISO(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    } catch {
      return dateString;
    }
  };

  const getSyncStatusBadgeVariant = (
    status: AssignmentScanSyncStatus,
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case AssignmentScanSyncStatus.SYNCED:
        return 'default';
      case AssignmentScanSyncStatus.PENDING:
        return 'secondary';
      case AssignmentScanSyncStatus.FAILED:
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getSyncStatusLabel = (status: AssignmentScanSyncStatus): string => {
    switch (status) {
      case AssignmentScanSyncStatus.SYNCED:
        return 'Синхронизировано';
      case AssignmentScanSyncStatus.PENDING:
        return 'Ожидает';
      case AssignmentScanSyncStatus.FAILED:
        return 'Ошибка';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-h1 gradient-text-blue">Сканы назначений</h1>
        <p className="text-muted-foreground mt-2 text-body-lg">
          История сканирований лекарств по назначениям пациентов
        </p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle>Фильтры и поиск</CardTitle>
          <CardDescription>
            Используйте фильтры для поиска нужных сканирований
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* ИИН пациента */}
            <div className="space-y-2">
              <label className="text-sm font-medium">ИИН пациента</label>
              <Input
                placeholder="920101300123 (12 цифр)"
                value={searchPatientIin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setSearchPatientIin(value);
                }}
                maxLength={12}
              />
            </div>

            {/* Фильтр по сотруднику */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Сотрудник</label>
              <Select
                value={selectedEmployeeId}
                onValueChange={setSelectedEmployeeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все сотрудники" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все сотрудники</SelectItem>
                  {!isLoadingEmployees &&
                    employeesData?.data.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.last_name} {employee.first_name}
                        {employee.middle_name ? ` ${employee.middle_name}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Статус синхронизации */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Статус</label>
              <Select
                value={selectedSyncStatus}
                onValueChange={setSelectedSyncStatus}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все статусы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="synced">Синхронизировано</SelectItem>
                  <SelectItem value="pending">Ожидает</SelectItem>
                  <SelectItem value="failed">Ошибка</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Сортировка */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Сортировка</label>
              <Select
                value={filters.sort || 'scanned_at:desc'}
                onValueChange={(value) =>
                  setFilters({ ...filters, sort: value, page: 1 })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scanned_at:desc">
                    Сначала новые
                  </SelectItem>
                  <SelectItem value="scanned_at:asc">
                    Сначала старые
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Вторая строка — даты + кнопки */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Дата с
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Дата по
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div className="flex items-end gap-2 md:col-span-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                <Search className="mr-2 h-4 w-4" />
                Применить
              </Button>
              <Button onClick={handleResetFilters} variant="outline">
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {data && !isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.total}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Всего сканирований
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-tco-blue-50 flex items-center justify-center">
                  <ScanBarcode className="h-6 w-6 text-tco-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.scans.length}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    На странице
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-success-50 flex items-center justify-center">
                  <Search className="h-6 w-6 text-success-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.page}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Страница из {data.totalPages}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-info-50 flex items-center justify-center">
                  <Search className="h-6 w-6 text-info-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.pageSize}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Записей на странице
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-warning-50 flex items-center justify-center">
                  <Search className="h-6 w-6 text-warning-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>История сканирований</CardTitle>
          <CardDescription>
            {data && `Показано ${data.scans.length} из ${data.total} записей`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={20} />
          ) : error ? (
            <ErrorState
              title="Ошибка загрузки данных"
              description={
                error instanceof Error
                  ? error.message
                  : 'Не удалось загрузить историю сканирований'
              }
              onAction={() => refetch()}
              actionLabel="Повторить"
            />
          ) : data && data.scans.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Назначение</TableHead>
                      <TableHead>Препарат</TableHead>
                      <TableHead>Пациент</TableHead>
                      <TableHead>Сотрудник</TableHead>
                      <TableHead>GTIN</TableHead>
                      <TableHead>Тип штрих-кода</TableHead>
                      <TableHead>Дата сканирования</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.scans.map((scan) => (
                      <TableRow key={scan.id}>
                        {/* Назначение ID */}
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            #{scan.drug_assignment_id}
                          </Badge>
                        </TableCell>

                        {/* Препарат */}
                        <TableCell>
                          <p className="font-medium text-sm">
                            {scan.drug_name}
                          </p>
                        </TableCell>

                        {/* Пациент */}
                        <TableCell>
                          <div className="space-y-1">
                            {scan.patient_name && (
                              <p className="font-medium text-sm">{scan.patient_name}</p>
                            )}
                            <p className="font-mono text-xs text-muted-foreground">
                              {scan.patient_iin}
                            </p>
                          </div>
                        </TableCell>

                        {/* Сотрудник */}
                        <TableCell>{scan.employee_name}</TableCell>

                        {/* GTIN */}
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-mono text-sm">{scan.gtin}</p>
                            {scan.serial_number && (
                              <p className="text-xs text-muted-foreground">
                                S/N: {scan.serial_number}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        {/* Тип штрих-кода */}
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {scan.barcode_type}
                          </Badge>
                        </TableCell>

                        {/* Дата сканирования */}
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm">
                              {formatDateTime(scan.scanned_at)}
                            </p>
                            {scan.batch_number && (
                              <p className="text-xs text-muted-foreground">
                                Партия: {scan.batch_number}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        {/* Статус */}
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant={getSyncStatusBadgeVariant(scan.sync_status as AssignmentScanSyncStatus)}>
                              {getSyncStatusLabel(scan.sync_status as AssignmentScanSyncStatus)}
                            </Badge>
                            {scan.sync_status === AssignmentScanSyncStatus.FAILED && scan.sync_error && (
                              <p className="text-xs text-destructive truncate max-w-[200px]" title={scan.sync_error}>
                                {scan.sync_error}
                              </p>
                            )}
                            {scan.sync_status === AssignmentScanSyncStatus.SYNCED && scan.synced_at && (
                              <p className="text-xs text-muted-foreground">
                                {formatDateTime(scan.synced_at)}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        {/* Действия */}
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedScan(scan)}
                            title="Просмотреть детали"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-tco-blue-50 mb-4">
                <ScanBarcode className="h-8 w-8 text-tco-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Нет сканирований
              </h3>
              <p className="text-muted-foreground mb-4">
                Сканирования назначений появятся здесь после их регистрации через мобильное приложение
              </p>
              {(searchPatientIin ||
                selectedEmployeeId !== 'all' ||
                selectedSyncStatus !== 'all' ||
                dateFrom ||
                dateTo) && (
                <Button onClick={handleResetFilters} variant="outline">
                  Сбросить фильтры
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {selectedScan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background p-6 rounded-lg max-w-2xl w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Детали сканирования</h2>
            <div className="space-y-2 text-sm">
              <p><strong>ID:</strong> {selectedScan.id}</p>
              <p><strong>Препарат:</strong> {selectedScan.drug_name}</p>
              <p><strong>GTIN:</strong> {selectedScan.gtin}</p>
              {selectedScan.serial_number && (
                <p><strong>Серийный номер:</strong> {selectedScan.serial_number}</p>
              )}
              {selectedScan.batch_number && (
                <p><strong>Партия:</strong> {selectedScan.batch_number}</p>
              )}
              <p><strong>Тип штрих-кода:</strong> {selectedScan.barcode_type}</p>
              {selectedScan.patient_name && (
                <p><strong>Пациент:</strong> {selectedScan.patient_name}</p>
              )}
              <p><strong>Пациент ИИН:</strong> {selectedScan.patient_iin}</p>
              <p><strong>ID пациента (стационар):</strong> {selectedScan.in_patient_id}</p>
              <p><strong>ID назначения:</strong> {selectedScan.drug_assignment_id}</p>
              <p><strong>Сотрудник:</strong> {selectedScan.employee_name}</p>
              <p><strong>Дата сканирования:</strong> {formatDateTime(selectedScan.scanned_at)}</p>
              <p><strong>Статус:</strong> {getSyncStatusLabel(selectedScan.sync_status as AssignmentScanSyncStatus)}</p>
              {selectedScan.sync_error && (
                <p><strong>Ошибка:</strong> <span className="text-destructive">{selectedScan.sync_error}</span></p>
              )}
              {selectedScan.raw_code && (
                <p><strong>Raw code:</strong> <span className="font-mono text-xs break-all">{selectedScan.raw_code}</span></p>
              )}
            </div>
            <Button onClick={() => setSelectedScan(null)} className="mt-4">
              Закрыть
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
